import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, api, fmtBytes, fmtTime, type Calibration, type StoredObject } from '../api'
import {
  computeSpectrogram, listeningGainDb, measureLevels, SILENT_DBFS,
  type Levels, type Spectrogram as Spec,
} from '../dsp'
import { type Playhead } from '../playhead'
import { connectPrimary, createListeningLimiter } from '../segments'
import { SpecLegend, StaticSpectrogram } from './Spectrogram'

interface UrlDoc {
  key: string
  kind: string
  size: number | null
  url: string
  expires_in: number
}

/* Above this, transform only the first slice. A 200 MB archive pull is a
 * legitimate object here, and decoding all of it to draw 1200 pixels would
 * freeze the tab for a minute to show what the first two minutes already say. */
const MAX_ANALYSE_BYTES = 40 * 1024 * 1024

/** A measured level. One decimal: the measurement is worth more than a whole
 *  number and less than two decimals of false precision. */
function fmtDbfs(db: number): string {
  return db <= SILENT_DBFS ? '−∞ dBFS' : `${db.toFixed(1)} dBFS`
}

/** An applied gain, always signed, so "+0.0 dB" and "0 dB" cannot be confused
 *  with a missing value. */
function fmtGain(db: number): string {
  return `${db >= 0 ? '+' : '−'}${Math.abs(db).toFixed(1)} dB`
}

/** How much to lift this clip for listening, in dB.
 *
 *  `listeningGainDb` is the one rule, shared with the live monitor and the
 *  multi-segment span player: a clip opened from the archive and the stream it
 *  was cut from must arrive at the ear at the same level. This wrapper exists
 *  only so the three call sites below read as they did. Never written to the
 *  file -- see the GainNode in `play`. */
function normalisationDb(l: Levels | null): number {
  return listeningGainDb(l)
}

/** Playback and spectrum for one archived object.
 *
 *  The bytes go straight from object storage to this browser on a presigned
 *  URL -- the receiver signs and steps aside (R7.2, R8.3). Decoding and the
 *  FFT happen here too, so listening to an archive costs the server nothing
 *  beyond one signature.
 */
export function ClipPlayer({
  object, calibration, onClose, playhead, onSource,
}: {
  object: StoredObject
  calibration: Calibration
  onClose: () => void
  /** Where the sound is, written every frame for whoever else is drawing it.
   *  Optional: the player is complete without one, and a caller that only
   *  wants to listen should not have to invent a playhead to do it. */
  playhead?: Playhead
  /** The node carrying the file's own samples, handed over each time playback
   *  starts, so a visualisation can tap it.
   *
   *  Deliberately the source and not the gain node. The gain is the listening
   *  lift -- 40 dB on a quiet hydrophone file -- and a picture drawn from it
   *  would show how loud we made it rather than how loud it was. */
  onSource?: (ctx: AudioContext, source: AudioNode) => void
}) {
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'undecodable'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [spec, setSpec] = useState<Spec | null>(null)
  const [truncated, setTruncated] = useState(false)
  const [progress, setProgress] = useState<number | null>(null)
  const [playing, setPlaying] = useState(false)
  const [url, setUrl] = useState<string | null>(null)
  /* What the file measures, and whether we are lifting it to listen.
   *
   * These are deliberately two separate things. `levels` describes the stored
   * bytes and never changes; `normalise` changes only what reaches the speaker.
   * Keeping them apart in the state is what keeps them apart on screen -- the
   * readout can then say both "this is what it is" and "this is what you are
   * hearing" without either being a guess. */
  const [levels, setLevels] = useState<Levels | null>(null)
  // Measured from the recording by the spectrogram, so the legend states what
  // is drawn rather than a pair of constants that stopped being true.
  const [range, setRange] = useState<[number, number]>([-95, -20])
  const [normalise, setNormalise] = useState(true)
  const gainRef = useRef<GainNode | null>(null)
  /* Held in a ref so a caller may pass a fresh closure every render
     without rebuilding the play callback and, with it, the audio graph. */
  const sourceCb = useRef(onSource)
  sourceCb.current = onSource
  const ph = useRef(playhead)
  ph.current = playhead

  const bufRef = useRef<AudioBuffer | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)
  const srcRef = useRef<AudioBufferSourceNode | null>(null)
  const startedAt = useRef(0)
  const offset = useRef(0)
  const raf = useRef(0)

  const teardown = useCallback(() => {
    cancelAnimationFrame(raf.current)
    /* Cleared, not left where it stopped. A stale playhead is a marker sitting
       on a map claiming a vessel is there now. */
    if (ph.current) { ph.current.t = null; ph.current.playing = false }
    try {
      srcRef.current?.stop()
    } catch {
      /* already stopped */
    }
    srcRef.current = null
    // Dropped with the context that owns it. Left dangling, the next clip would
    // connect a fresh source to a gain node belonging to a closed context and
    // play nothing at all.
    gainRef.current = null
    void ctxRef.current?.close()
    ctxRef.current = null
  }, [])

  useEffect(() => teardown, [teardown])

  useEffect(() => {
    let cancelled = false
    setState('loading')
    setSpec(null)
    setError(null)
    setTruncated(false)
    setUrl(null)

    ;(async () => {
      try {
        // download=1 binds a Content-Disposition into the signature, which is
        // what makes the Save button below actually save. The `download`
        // attribute on an <a> is ignored cross-origin, and this URL points at
        // Hetzner while the page is served from app.phoniq.no -- so Save had
        // been opening the audio in a tab under a key-shaped name.
        //
        // Harmless for playback: fetch() ignores Content-Disposition, so one
        // URL still serves both paths and this stays a single round trip.
        const doc = await api.get<UrlDoc>(
          `/v1/objects/url?key=${encodeURIComponent(object.key)}&download=1`,
        )
        if (cancelled) return
        setUrl(doc.url)

        // Cross-origin to Hetzner. This is the fetch that needs the bucket CORS
        // policy the receiver applies at startup; without it the request fails
        // in the browser while every server-side check passes.
        const res = await fetch(doc.url)
        if (!res.ok) throw new Error(`storage returned ${res.status}`)
        let bytes = await res.arrayBuffer()
        if (cancelled) return

        if (bytes.byteLength > MAX_ANALYSE_BYTES) {
          bytes = bytes.slice(0, MAX_ANALYSE_BYTES)
          setTruncated(true)
        }

        const ctx = new AudioContext()
        ctxRef.current = ctx
        let buf: AudioBuffer
        try {
          buf = await ctx.decodeAudioData(bytes)
        } catch {
          // Not every stored object is audio: `meta` and `features` are
          // JSON/Parquet roll-ups, and a truncated slice of a container format
          // may not decode either. Say so plainly and offer the download.
          if (cancelled) return
          setState('undecodable')
          return
        }
        if (cancelled) return

        bufRef.current = buf
        const chan = buf.getChannelData(0)
        // Measured before the FFT and from the same samples the spectrogram is
        // drawn from, so the numbers on screen and the picture describe one
        // thing. Both describe the file as stored; neither is affected by the
        // normalisation applied below.
        setLevels(measureLevels(chan))
        setSpec(computeSpectrogram(chan, buf.sampleRate))
        setState('ready')
      } catch (e) {
        if (cancelled) return
        setError(e instanceof ApiError ? e.detail ?? e.code : String(e))
        setState('error')
      }
    })()

    return () => {
      cancelled = true
      teardown()
    }
  }, [object.key, teardown])

  const tick = useCallback(() => {
    const ctx = ctxRef.current
    const buf = bufRef.current
    if (!ctx || !buf) return
    const t = offset.current + (ctx.currentTime - startedAt.current)
    if (t >= buf.duration) {
      setPlaying(false)
      setProgress(null)
      offset.current = 0
      if (ph.current) { ph.current.t = null; ph.current.playing = false }
      return
    }
    setProgress(t / buf.duration)
    if (ph.current) {
      ph.current.t = t
      ph.current.duration = buf.duration
      ph.current.playing = true
    }
    raf.current = requestAnimationFrame(tick)
  }, [])

  const play = useCallback(
    (from?: number) => {
      const ctx = ctxRef.current
      const buf = bufRef.current
      if (!ctx || !buf) return
      try {
        srcRef.current?.stop()
      } catch {
        /* not started */
      }
      void ctx.resume()
      const src = ctx.createBufferSource()
      src.buffer = buf

      /* The only place normalisation happens: on the way to the speaker.
       *
       * src.buffer is the decoded file and is handed to the graph untouched, so
       * the samples the spectrogram measured are the samples that exist. The
       * gain is a property of this playback, not of the recording -- which is
       * the whole separation being built here. Nothing written to storage, and
       * nothing offered for download from this component, passes through it.
       *
       * The node is reused across plays so that toggling mid-playback ramps
       * rather than restarting. */
      let gain = gainRef.current
      if (!gain) {
        gain = ctx.createGain()
        // Gain -> limiter -> speaker, the same chain the live graph uses. At
        // the boosts this applies a transient can arrive at full scale in
        // someone's headphones, and an archive is where you go looking for
        // transients on purpose.
        gain.connect(createListeningLimiter(ctx)).connect(ctx.destination)
        gainRef.current = gain
      }
      gain.gain.value = Math.pow(10, (normalise ? normalisationDb(levels) : 0) / 20)
      // One channel to both ears, not the file as stereo. These are
      // single-hydrophone recordings written to a stereo-only codec, and the
      // second channel is a low-gain copy in antiphase -- measured at -0.96
      // correlation on this fleet. The analysis above already reads channel 0
      // only; playback was the half that still passed both through, so a clip
      // opened from the archive arrived in one side of the headset.
      connectPrimary(ctx, src, gain)
      // The tap goes on the same source, in parallel with the listening chain,
      // so a visualiser sees the file and the limiter never sees the visualiser.
      sourceCb.current?.(ctx, src)
      offset.current = from ?? offset.current
      startedAt.current = ctx.currentTime
      src.start(0, offset.current)
      src.onended = () => setPlaying(false)
      srcRef.current = src
      setPlaying(true)
      cancelAnimationFrame(raf.current)
      raf.current = requestAnimationFrame(tick)
    },
    [tick, normalise, levels],
  )

  /* Toggling while a clip is playing ramps rather than jumping.
   *
   * At the boosts this applies -- 40 dB and more on a quiet hydrophone file --
   * an instant gain change is a step discontinuity, which is a click, and the
   * click is louder than anything in the recording. 30 ms is below the
   * threshold of hearing it as a fade and well above the threshold of hearing
   * it as a click. */
  useEffect(() => {
    const g = gainRef.current
    if (!g) return
    const target = Math.pow(10, (normalise ? normalisationDb(levels) : 0) / 20)
    const t = g.context.currentTime
    g.gain.cancelScheduledValues(t)
    g.gain.setTargetAtTime(target, t, 0.03)
  }, [normalise, levels])

  const pause = useCallback(() => {
    const ctx = ctxRef.current
    if (!ctx) return
    offset.current += ctx.currentTime - startedAt.current
    try {
      srcRef.current?.stop()
    } catch {
      /* already stopped */
    }
    cancelAnimationFrame(raf.current)
    setPlaying(false)
    if (ph.current) { ph.current.t = null; ph.current.playing = false }
  }, [])

  const name = object.key.split('/').pop() ?? object.key

  /* Bring the player to the person who opened it.
   *
   * The player renders at the top of the tab and a clip can be chosen from a
   * calendar day, a timeline marker, or a table row well below the fold. From
   * down there, opening one changed nothing visible: the request went out, the
   * spectrogram drew, and the operator was still looking at the list wondering
   * whether the click had registered.
   *
   * Focus moves too, not just the viewport. A scroll is invisible to anyone
   * using a screen reader or driving by keyboard, and they have exactly the
   * same problem -- the thing they asked for appeared somewhere they are not.
   * The heading is the right target rather than the play button: it announces
   * what opened, and it does not arm a control that the person has not chosen
   * to press yet.
   *
   * Keyed on object.key, so choosing a different clip while one is open moves
   * again rather than only on first mount. */
  const headRef = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    const el = headRef.current
    if (!el) return
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    el.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' })
    // preventScroll: the scroll above is already doing it, and letting focus
    // scroll as well fights the smooth animation and lands somewhere else.
    el.focus({ preventScroll: true })
  }, [object.key])

  return (
    <div className="card flush">
      <div className="card-head">
        {/* tabIndex -1 so it can take focus programmatically without joining
            the tab order: it is a destination, not a stop on the way. */}
        <h2
          ref={headRef}
          tabIndex={-1}
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', outline: 'none' }}
        >
          {name}
        </h2>
        <span className="badge">{object.kind}</span>
        <span className="faint num" style={{ fontSize: 12 }}>
          {fmtBytes(object.size)} · {fmtTime(object.ts)}
        </span>
        <div className="cta">
          {state === 'ready' && (
            <button className="btn primary" onClick={() => (playing ? pause() : play())}>
              {playing ? 'Pause' : 'Play'}
            </button>
          )}
          {url && (
            // Downloaded straight from object storage on the presigned URL --
            // the receiver signs and steps aside, exactly as playback does.
            <a className="btn" href={url} download={name}>
              Save
            </a>
          )}
          <button className="btn ghost" onClick={onClose} aria-label="Close player">
            Close
          </button>
        </div>
      </div>

      <div className="card-body stack">
        {state === 'loading' && (
          <>
            <div className="skeleton" style={{ height: 300 }} />
            <span className="faint">Fetching from storage and transforming…</span>
          </>
        )}

        {state === 'error' && (
          <div className="notice bad">Could not load this object: {error}</div>
        )}

        {state === 'undecodable' && (
          <div className="empty">
            <h3>Not audio this browser can decode</h3>
            <p>
              This kind holds measurements rather than sound, or is in a
              container the browser has no decoder for.
            </p>
            {url && (
              <a className="btn" href={url} download={name}>
                Save a copy
              </a>
            )}
          </div>
        )}

        {state === 'ready' && spec && (
          <>
            <StaticSpectrogram
              spec={spec}
              height={300}
              progress={progress}
              onSeek={(f) => play(f * spec.duration)}
              onRange={(a, b) => setRange([a, b])}
            />
            <SpecLegend
              basis={calibration.basis}
              absolute={calibration.absolute}
              sampleRate={spec.sampleRate}
              minDb={range[0]}
              maxDb={range[1]}
              note={`${spec.duration.toFixed(1)} s · ${spec.fftSize}-point · scale follows the recording`}
            />

            {/* What was done to the sound, stated next to what the sound is.
                The two are separate on purpose: the left half is a property of
                this playback and changes when the button is pressed; the right
                half is a property of the file and never changes. Without both,
                a normalised clip is just a loud noise of unknown origin. */}
            {levels && (
              <div className="row" style={{ flexWrap: 'wrap', gap: 10 }}>
                <button
                  className={`btn${normalise ? ' primary' : ''}`}
                  aria-pressed={normalise}
                  disabled={levels.silent}
                  onClick={() => setNormalise((v) => !v)}
                >
                  {normalise ? 'Normalised' : 'Original level'}
                </button>

                <span className="num" aria-live="polite">
                  {levels.silent
                    ? 'no signal to lift'
                    : `${normalise ? fmtGain(normalisationDb(levels)) : '0 dB'} applied for playback`}
                </span>

                <span className="faint num">
                  {levels.silent ? (
                    'file: digital silence'
                  ) : (
                    <>
                      file: {fmtDbfs(levels.rmsDbfs)} RMS, {fmtDbfs(levels.peakDbfs)} peak
                      {!calibration.absolute && ' · relative'}
                    </>
                  )}
                </span>

                {levels.clipped && (
                  <span className="badge alert">clipped at source</span>
                )}
              </div>
            )}

            {/* Said once, plainly. A number that looks measured but is only
                comparable with itself is the failure rule 5 exists to prevent,
                and this panel now shows several of them. */}
            {levels && !levels.silent && (
              <div className="faint" style={{ fontSize: 12 }}>
                Levels are dBFS — the numbers in the file, not sound pressure.
                Normalisation changes only what you hear: the spectrogram, the
                figures above and the saved copy are all the unmodified
                recording.
                {!calibration.absolute &&
                  ' This mission is uncalibrated, so these may be compared with' +
                    ' itself over time but not against another site.'}
              </div>
            )}

            {truncated && (
              <div className="notice">
                Showing the first {fmtBytes(MAX_ANALYSE_BYTES)} of a larger
                object. Download it for the whole recording.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
